import React, { useEffect, useState, useCallback } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import ImageUploader from '../components/ImageUploader';
import { 
  LayoutDashboard, Users, Store, Package, ShoppingCart, DollarSign,
  CheckCircle, XCircle, Clock, AlertCircle, BadgeCheck, Shield,
  Download, Eye, Smartphone, MessageSquare, Mail, Fuel, ListChecks, Target, Wallet, Pencil, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth, API } from '../App';
import QuotesManager from '../components/QuotesManager';
import TestimonialsAdmin from '../components/TestimonialsAdmin';
import ReferralsAdmin from '../components/ReferralsAdmin';
import FeaturedStoryAdmin from '../components/FeaturedStoryAdmin';
import BlogAdmin from '../components/BlogAdmin';
import EmailBroadcastAdmin from '../components/EmailBroadcastAdmin';
import CommodityProductAdmin from '../components/CommodityProductAdmin';
import VendorOnboardingAdmin from '../components/VendorOnboardingAdmin';
import CampaignRoasAdmin from '../components/CampaignRoasAdmin';
import ReferrerPayoutsAdmin from '../components/ReferrerPayoutsAdmin';
import ProductBulkAdmin from '../components/ProductBulkAdmin';

// Tailwind class lookup for vendor status badges — avoids nested ternaries
const VENDOR_STATUS_BADGE_CLASS = {
  approved: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  rejected: 'bg-red-100 text-red-800',
  suspended: 'bg-gray-100 text-gray-800',
  default: 'bg-gray-100 text-gray-800',
};

const VerificationBadge = ({ verification }) => {
  if (verification === 'trusted') {
    return (
      <Badge className="bg-amber-100 text-amber-800 gap-1">
        <Shield className="h-3 w-3" />
        Trusted
      </Badge>
    );
  }
  if (verification === 'verified') {
    return (
      <Badge className="bg-green-100 text-green-800 gap-1">
        <BadgeCheck className="h-3 w-3" />
        Verified
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="gap-1">
      Unverified
    </Badge>
  );
};

// Admin Overview
function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recentVendors, setRecentVendors] = useState([]);
  const [finance, setFinance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [statsRes, analyticsRes, recentRes, financeRes] = await Promise.all([
          axios.get(`${API}/admin/stats`),
          axios.get(`${API}/admin/analytics/summary`).catch(() => ({ data: null })),
          axios.get(`${API}/admin/vendors/recent?days=7`).catch(() => ({ data: null })),
          axios.get(`${API}/admin/finance/summary`).catch(() => ({ data: null })),
        ]);
        setStats(statsRes.data);
        setAnalytics(analyticsRes.data);
        setRecentVendors(recentRes.data?.vendors || []);
        setFinance(financeRes.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {['ov1','ov2','ov3','ov4','ov5','ov6'].map((k) => (
          <div key={k} className="h-28 bg-muted rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  const statCards = [
    { label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'primary' },
    { label: 'Total Vendors', value: stats?.total_vendors || 0, icon: Store, color: 'secondary' },
    { label: 'Pending Vendors', value: stats?.pending_vendors || 0, icon: Clock, color: 'yellow' },
    { label: 'Total Products', value: stats?.total_products || 0, icon: Package, color: 'accent' },
    { label: 'Total Orders', value: stats?.total_orders || 0, icon: ShoppingCart, color: 'blue' },
    { label: 'Revenue', value: `$${(stats?.total_revenue || 0).toFixed(2)}`, icon: DollarSign, color: 'green' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 bg-${stat.color}/10 rounded-lg flex items-center justify-center shrink-0`}>
                    <Icon className={`h-5 w-5 text-${stat.color}`} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                    <p className="text-xl font-heading font-semibold">{stat.value}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {stats?.pending_vendors > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            <p className="text-sm text-yellow-800">
              You have {stats.pending_vendors} vendor(s) awaiting approval.{' '}
              <Link to="/admin/vendors" className="font-medium underline">Review now</Link>
            </p>
          </CardContent>
        </Card>
      )}

      {/* Finance breakdown — Stripe Connect earnings at a glance */}
      {finance && (
        <Card data-testid="finance-summary" className="border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="flex items-center gap-2 text-emerald-900">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                  Earnings Breakdown
                </CardTitle>
                <CardDescription className="text-emerald-800/80">
                  Live numbers from {finance.completed_order_count} completed Stripe order(s).
                </CardDescription>
              </div>
              <a
                href="https://dashboard.stripe.com/connect/application_fees"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-emerald-700 underline hover:text-emerald-900"
              >
                View in Stripe →
              </a>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white rounded-lg p-4 border border-emerald-100">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Gross Revenue</p>
                <p className="text-2xl font-heading font-semibold text-emerald-900 mt-1" data-testid="finance-gross">
                  ${finance.gross_revenue.toFixed(2)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Avg order: ${finance.avg_order_value.toFixed(2)}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-emerald-200 ring-1 ring-emerald-100">
                <p className="text-xs text-emerald-700 uppercase tracking-wider font-semibold">Your Platform Fees</p>
                <p className="text-2xl font-heading font-semibold text-emerald-700 mt-1" data-testid="finance-platform-fees">
                  ${finance.platform_fees_collected.toFixed(2)}
                </p>
                <p className="text-xs text-emerald-700 mt-1">
                  Datasamllc&apos;s share
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-emerald-100">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Vendors&apos; Share</p>
                <p className="text-2xl font-heading font-semibold text-gray-900 mt-1" data-testid="finance-vendor-share">
                  ${finance.vendor_gross_share.toFixed(2)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Pending: ${finance.vendor_pending_payout.toFixed(2)}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-emerald-100">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Stripe Fees (est.)</p>
                <p className="text-2xl font-heading font-semibold text-gray-700 mt-1" data-testid="finance-stripe-fees">
                  ${finance.stripe_processing_fees_estimate.toFixed(2)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  ~2.9% + $0.30/order
                </p>
              </div>
            </div>
            <div className="mt-4 bg-white border border-emerald-100 rounded-md p-3 text-xs text-emerald-900 leading-relaxed">
              <strong>How this works:</strong> Stripe Connect splits each sale instantly — your platform fee goes straight into Datasamllc&apos;s Stripe balance, vendors receive the rest in their own Connect accounts. Both sides are paid out to their own banks on Stripe&apos;s normal schedule.
              {' · '}
              <span className="text-emerald-700">
                Founding Vendors claimed: <strong>{finance.founding_vendors_claimed} / {finance.founding_vendor_limit}</strong>
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Signup Counters */}
      <div data-testid="signup-counters">
        <h2 className="text-lg font-heading font-semibold mb-3">Signups at a Glance</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Store className="h-4 w-4 text-primary" />
                <h3 className="font-heading font-semibold text-sm">Vendors</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-2xl font-heading font-semibold" data-testid="vendors-today">{stats?.signups?.vendors?.today ?? 0}</p>
                  <p className="text-xs text-muted-foreground">Today</p>
                </div>
                <div>
                  <p className="text-2xl font-heading font-semibold" data-testid="vendors-week">{stats?.signups?.vendors?.week ?? 0}</p>
                  <p className="text-xs text-muted-foreground">This Week</p>
                </div>
                <div>
                  <p className="text-2xl font-heading font-semibold" data-testid="vendors-month">{stats?.signups?.vendors?.month ?? 0}</p>
                  <p className="text-xs text-muted-foreground">This Month</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-secondary/30 bg-secondary/5">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Users className="h-4 w-4 text-secondary" />
                <h3 className="font-heading font-semibold text-sm">Buyers</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-2xl font-heading font-semibold" data-testid="customers-today">{stats?.signups?.customers?.today ?? 0}</p>
                  <p className="text-xs text-muted-foreground">Today</p>
                </div>
                <div>
                  <p className="text-2xl font-heading font-semibold" data-testid="customers-week">{stats?.signups?.customers?.week ?? 0}</p>
                  <p className="text-xs text-muted-foreground">This Week</p>
                </div>
                <div>
                  <p className="text-2xl font-heading font-semibold" data-testid="customers-month">{stats?.signups?.customers?.month ?? 0}</p>
                  <p className="text-xs text-muted-foreground">This Month</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recent Vendor Signups */}
      <Card data-testid="recent-vendors-widget">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Recent Vendor Signups</CardTitle>
              <CardDescription>New vendors in the last 7 days</CardDescription>
            </div>
            <Link to="/admin/vendors" className="text-sm text-primary hover:underline">
              View all →
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {recentVendors.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No new vendor signups in the last 7 days.
            </p>
          ) : (
            <div className="space-y-3">
              {recentVendors.slice(0, 8).map((v) => (
                <div
                  key={v.vendor_id}
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                  data-testid={`recent-vendor-${v.vendor_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate">{v.business_name}</p>
                      {v.is_founding_vendor && (
                        <Badge variant="secondary" className="text-xs shrink-0">Founding</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {v.owner_name || '—'} · {v.owner_email} {v.country && `· ${v.country}`}
                    </p>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <Badge
                      className={
                        VENDOR_STATUS_BADGE_CLASS[v.status] || VENDOR_STATUS_BADGE_CLASS.default
                      }
                    >
                      {v.status}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(v.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* App & Traffic Analytics */}
      <div>
        <h2 className="text-lg font-heading font-semibold mb-3">App & Traffic Analytics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="analytics-cards">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                  <Download className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">App Installs (PWA)</p>
                  <p className="text-xl font-heading font-semibold" data-testid="pwa-installs-count">
                    {analytics?.pwa_installs ?? 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center shrink-0">
                  <Eye className="h-5 w-5 text-secondary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Page Views (30d)</p>
                  <p className="text-xl font-heading font-semibold" data-testid="page-views-count">
                    {analytics?.page_views_30d ?? 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center shrink-0">
                  <Smartphone className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Unique Visitors (30d)</p>
                  <p className="text-xl font-heading font-semibold" data-testid="unique-visitors-count">
                    {analytics?.unique_visitors_30d ?? 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {analytics?.daily_installs?.length > 0 && (
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-sm">Recent Installs (Last 7 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {analytics.daily_installs.map((d) => (
                  <div key={d._id} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{d._id}</span>
                    <span className="font-medium">{d.count} install{d.count !== 1 ? 's' : ''}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// Vendor Management
function AdminVendors() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchVendors = useCallback(async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axios.get(`${API}/admin/vendors${params}`);
      setVendors(response.data.vendors || []);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const handleApprove = async (vendorId) => {
    try {
      await axios.put(`${API}/admin/vendors/${vendorId}/approve`);
      toast.success('Vendor approved!');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to approve vendor');
    }
  };

  const handleReject = async (vendorId) => {
    if (!window.confirm('Are you sure you want to reject this vendor?')) return;
    try {
      await axios.put(`${API}/admin/vendors/${vendorId}/reject`);
      toast.success('Vendor rejected');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to reject vendor');
    }
  };

  const handleVerify = async (vendorId, verification) => {
    try {
      await axios.put(`${API}/admin/vendors/${vendorId}/verify?verification=${verification}`);
      toast.success(`Vendor marked as ${verification}`);
      fetchVendors();
    } catch (error) {
      toast.error('Failed to update verification');
    }
  };

  const handleToggleFeatured = async (vendorId, isFeatured) => {
    try {
      await axios.put(`${API}/admin/vendors/${vendorId}/feature?is_featured=${isFeatured}`);
      toast.success(isFeatured ? '✨ Pinned to homepage rotation' : 'Removed from featured rotation');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to update featured status');
    }
  };

  const [sendingReminders, setSendingReminders] = useState(false);
  const handleSendStripeReminders = async () => {
    if (!window.confirm("Send a Stripe-setup reminder email to every approved vendor who hasn't connected Stripe yet?")) {
      return;
    }
    setSendingReminders(true);
    try {
      const r = await axios.post(`${API}/admin/vendors/send-stripe-reminders`);
      toast.success(r.data.message || 'Reminders sent.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const [syncingPrintful, setSyncingPrintful] = useState(false);
  const handleSyncPrintful = async () => {
    if (!window.confirm('Pull all your Printful sync products into the Christ Kingdom catalog? This is safe to run multiple times.')) {
      return;
    }
    setSyncingPrintful(true);
    try {
      const r = await axios.post(`${API}/admin/printful/sync-catalog`);
      toast.success(r.data.message || 'Printful catalog synced.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Printful sync failed');
    } finally {
      setSyncingPrintful(false);
    }
  };

  const [sendingFoundingLetter, setSendingFoundingLetter] = useState(false);
  const handleSendFoundingLetter = async () => {
    setSendingFoundingLetter(true);
    try {
      // Step 1: dry-run preview so admin knows exactly who will be emailed
      const preview = await axios.post(`${API}/admin/vendors/send-founding-letter?dry_run=true`);
      const d = preview.data || {};
      const previewMsg =
        `Founding Vendor Letter — preview\n\n` +
        `Will email: ${d.would_send} real vendor(s)\n` +
        `Skipped (test emails): ${d.skipped_test_emails}\n` +
        `Skipped (no email): ${d.skipped_missing_email}\n` +
        `Skipped (already sent): ${d.skipped_already_sent}\n\n` +
        (d.would_send > 0
          ? `Proceed and send the personal Founding Vendor letter to ${d.would_send} vendor(s) now?`
          : `No real vendors to email right now.`);
      if (!window.confirm(previewMsg) || d.would_send === 0) {
        setSendingFoundingLetter(false);
        return;
      }
      const r = await axios.post(`${API}/admin/vendors/send-founding-letter`);
      toast.success(r.data.message || 'Founding letter sent.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send Founding letter');
    } finally {
      setSendingFoundingLetter(false);
    }
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const handleDownloadFoundingPdf = async () => {
    setDownloadingPdf(true);
    try {
      const r = await axios.get(`${API}/admin/vendors/founding-letter.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'founding-vendor-letter.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Founding Vendor letter downloaded.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const [resendingVendorId, setResendingVendorId] = useState(null);
  const handleResendEmail = async (vendorId, template, label) => {
    if (!window.confirm(`Resend the "${label}" email to this vendor?`)) return;
    setResendingVendorId(`${vendorId}:${template}`);
    try {
      const r = await axios.post(`${API}/admin/vendors/${vendorId}/resend-email?template=${template}`);
      toast.success(r.data.message || `${label} email resent.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resend email');
    } finally {
      setResendingVendorId(null);
    }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    suspended: 'bg-gray-100 text-gray-800',
  };

  if (loading) {
    return <div className="h-64 bg-muted rounded-lg animate-pulse" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-heading font-medium">Vendor Management</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={handleSyncPrintful}
            disabled={syncingPrintful}
            data-testid="sync-printful-btn"
            title="Pull your Printful sync products into the Christ Kingdom catalog"
          >
            {syncingPrintful ? 'Syncing…' : '🖨️ Sync Printful'}
          </Button>
          <Button
            variant="outline"
            onClick={handleSendFoundingLetter}
            disabled={sendingFoundingLetter}
            data-testid="send-founding-letter-btn"
            title="Email the personal Founding Vendor letter to your founding vendors"
          >
            {sendingFoundingLetter ? 'Sending…' : '✉️ Send Founding Letter'}
          </Button>
          <Button
            variant="outline"
            onClick={handleDownloadFoundingPdf}
            disabled={downloadingPdf}
            data-testid="download-founding-pdf-btn"
            title="Download the Founding Vendor letter as a printable PDF flyer"
          >
            {downloadingPdf ? 'Preparing…' : '📄 Founding Letter PDF'}
          </Button>
          <Button
            variant="outline"
            onClick={handleSendStripeReminders}
            disabled={sendingReminders}
            data-testid="send-stripe-reminders-btn"
            title="Send a 'Finish Stripe Setup' email to every vendor who hasn't connected yet"
          >
            {sendingReminders ? 'Sending…' : '📧 Send Stripe Reminders'}
          </Button>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]" data-testid="vendor-status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {vendors.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Store className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No vendors found</p>
          </CardContent>
        </Card>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Business Name</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Commission</TableHead>
              <TableHead>Total Sales</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Verification</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {vendors.map((vendor) => (
              <TableRow key={vendor.vendor_id} data-testid={`vendor-row-${vendor.vendor_id}`}>
                <TableCell className="font-medium">{vendor.business_name}</TableCell>
                <TableCell>{vendor.country}</TableCell>
                <TableCell>{(vendor.commission_rate * 100).toFixed(0)}%</TableCell>
                <TableCell>${vendor.total_sales.toFixed(2)}</TableCell>
                <TableCell>
                  <Badge className={statusColors[vendor.status]}>{vendor.status}</Badge>
                </TableCell>
                <TableCell>
                  <VerificationBadge verification={vendor.verification} />
                </TableCell>
                <TableCell>
                  <div className="flex gap-2 flex-wrap">
                    {vendor.status === 'pending' && (
                      <>
                        <Button 
                          size="sm" 
                          onClick={() => handleApprove(vendor.vendor_id)}
                          data-testid={`approve-vendor-${vendor.vendor_id}`}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Approve
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => handleReject(vendor.vendor_id)}
                          data-testid={`reject-vendor-${vendor.vendor_id}`}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </>
                    )}
                    {vendor.status === 'approved' && (
                      <>
                        <Select 
                          value={vendor.verification || 'unverified'} 
                          onValueChange={(value) => handleVerify(vendor.vendor_id, value)}
                        >
                          <SelectTrigger className="w-[130px] h-8" data-testid={`verify-vendor-${vendor.vendor_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unverified">Unverified</SelectItem>
                            <SelectItem value="verified">Verified</SelectItem>
                            <SelectItem value="trusted">Trusted</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          variant={vendor.is_featured ? 'default' : 'outline'}
                          onClick={() => handleToggleFeatured(vendor.vendor_id, !vendor.is_featured)}
                          data-testid={`feature-vendor-${vendor.vendor_id}`}
                          title={vendor.is_featured ? 'Pinned on homepage — click to unpin' : 'Pin to homepage Featured Vendors rotation'}
                        >
                          ✨ {vendor.is_featured ? 'Featured' : 'Pin'}
                        </Button>
                        <Select
                          value=""
                          onValueChange={(value) => {
                            const labels = {
                              welcome: 'Welcome (Day 0)',
                              founding_letter: 'Founding Vendor Letter',
                              day1: 'Day 1 nudge',
                              day3: 'Day 3 tips',
                              day7: 'Week 1 check-in',
                              stripe_reminder: 'Stripe setup reminder',
                            };
                            handleResendEmail(vendor.vendor_id, value, labels[value] || value);
                          }}
                          disabled={!!resendingVendorId && resendingVendorId.startsWith(`${vendor.vendor_id}:`)}
                        >
                          <SelectTrigger
                            className="w-[150px] h-8"
                            data-testid={`resend-email-${vendor.vendor_id}`}
                            title="Resend a specific onboarding email to this vendor"
                          >
                            <SelectValue placeholder={resendingVendorId && resendingVendorId.startsWith(`${vendor.vendor_id}:`) ? 'Sending…' : '✉️ Resend…'} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="welcome">Welcome (Day 0)</SelectItem>
                            <SelectItem value="founding_letter">Founding Letter</SelectItem>
                            <SelectItem value="day1">Day 1 nudge</SelectItem>
                            <SelectItem value="day3">Day 3 tips</SelectItem>
                            <SelectItem value="day7">Week 1 check-in</SelectItem>
                            <SelectItem value="stripe_reminder">Stripe reminder</SelectItem>
                          </SelectContent>
                        </Select>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// Product Moderation
// Available product categories (match ProductBulkAdmin.jsx and backend)
const PRODUCT_CATEGORIES = [
  'Fashion & Apparel', 'Hats & Headwear', 'Jewelry & Accessories',
  'Shoes & Footwear', 'Bags & Accessories', 'Beauty & Skincare',
  'Home & Decor', 'Books & Media', 'Agriculture & Farming',
  'Auto & Transportation', 'Electronics & Tech', 'Kids & Family',
  'Food & Groceries', 'Other',
];

// Edit modal — lets admin change name/description/price/category/qty/images
function EditProductDialog({ product, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (product) {
      setForm({
        name: product.name || '',
        description: product.description || '',
        price: String(product.price ?? ''),
        category: product.category || 'Other',
        quantity: Number(product.quantity ?? 100),
        images: Array.isArray(product.images) ? [...product.images] : [],
      });
    }
  }, [product]);

  if (!form || !product) return null;

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Product name is required');
      return;
    }
    if (!form.price || isNaN(Number(form.price)) || Number(form.price) < 0) {
      toast.error('Enter a valid price');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/products/${product.product_id}`, {
        name: form.name.trim(),
        description: form.description.trim(),
        price: Number(form.price),
        category: form.category,
        quantity: Number(form.quantity) || 0,
        images: form.images,
      });
      toast.success('Product updated');
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update product');
    } finally {
      setSaving(false);
    }
  };

  const removeImage = (idx) => {
    setForm({ ...form, images: form.images.filter((_, i) => i !== idx) });
  };

  const addImageUrl = (url) => {
    const u = (url || '').trim();
    if (!u) return;
    setForm({ ...form, images: [...form.images, u] });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-product-dialog">
        <DialogHeader>
          <DialogTitle>Edit Product</DialogTitle>
          <DialogDescription>Update product details, images, and pricing.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium">Name</label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="edit-product-name"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Description</label>
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="edit-product-description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Price (USD)</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                data-testid="edit-product-price"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Quantity</label>
              <Input
                type="number"
                min="0"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                data-testid="edit-product-quantity"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Category</label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="edit-product-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRODUCT_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium">Images ({form.images.length})</label>
            <div className="grid grid-cols-4 gap-2 mt-2">
              {form.images.map((url, i) => (
                <div key={`${url}-${i}`} className="relative aspect-square rounded border overflow-hidden group">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                    data-testid={`edit-product-remove-image-${i}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-3 space-y-2">
              <ImageUploader onUploaded={addImageUrl} />
              <div className="flex gap-2">
                <Input
                  placeholder="Or paste an image URL and press Enter"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addImageUrl(e.target.value);
                      e.target.value = '';
                    }
                  }}
                  data-testid="edit-product-image-url"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-product-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="edit-product-save">
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [editingProduct, setEditingProduct] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(null);

  const fetchProducts = useCallback(async () => {
    try {
      const params = filter !== 'all' ? `?is_active=${filter}` : '';
      const response = await axios.get(`${API}/admin/products${params}`);
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleToggleActive = async (productId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/products/${productId}/moderate?is_active=${!currentStatus}`);
      toast.success(`Product ${!currentStatus ? 'activated' : 'deactivated'}`);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  const handleDelete = async () => {
    if (!deletingProduct) return;
    try {
      await axios.delete(`${API}/products/${deletingProduct.product_id}`);
      toast.success(`Deleted "${deletingProduct.name}"`);
      setDeletingProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    }
  };

  if (loading) {
    return <div className="h-64 bg-muted rounded-lg animate-pulse" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-heading font-medium">Product Moderation</h2>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px]" data-testid="admin-products-filter">
            <SelectValue placeholder="All Products" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Products</SelectItem>
            <SelectItem value="true">Active</SelectItem>
            <SelectItem value="false">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {products.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No products found</p>
          </CardContent>
        </Card>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Vendor</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.product_id} data-testid={`admin-product-row-${product.product_id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded bg-muted overflow-hidden">
                      <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                    </div>
                    <span className="font-medium">{product.name}</span>
                  </div>
                </TableCell>
                <TableCell>{product.vendor_name}</TableCell>
                <TableCell>{product.category}</TableCell>
                <TableCell>${product.price.toFixed(2)}</TableCell>
                <TableCell>
                  <Badge variant={product.is_active ? 'default' : 'secondary'}>
                    {product.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right space-x-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingProduct(product)}
                    data-testid={`admin-product-edit-${product.product_id}`}
                  >
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant={product.is_active ? 'destructive' : 'default'}
                    onClick={() => handleToggleActive(product.product_id, product.is_active)}
                    data-testid={`admin-product-toggle-${product.product_id}`}
                  >
                    {product.is_active ? 'Hide' : 'Show'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDeletingProduct(product)}
                    data-testid={`admin-product-delete-${product.product_id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <EditProductDialog
        product={editingProduct}
        open={!!editingProduct}
        onOpenChange={(v) => !v && setEditingProduct(null)}
        onSaved={fetchProducts}
      />

      <Dialog open={!!deletingProduct} onOpenChange={(v) => !v && setDeletingProduct(null)}>
        <DialogContent data-testid="delete-product-dialog">
          <DialogHeader>
            <DialogTitle>Delete Product?</DialogTitle>
            <DialogDescription>
              This permanently removes "<strong>{deletingProduct?.name}</strong>" from your store. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingProduct(null)} data-testid="delete-product-cancel">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="delete-product-confirm">
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Order Management
function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchOrders = useCallback(async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axios.get(`${API}/admin/orders${params}`);
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleUpdateStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${status}`);
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    processing: 'bg-blue-100 text-blue-800',
    shipped: 'bg-purple-100 text-purple-800',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    refunded: 'bg-gray-100 text-gray-800',
  };

  if (loading) {
    return <div className="h-64 bg-muted rounded-lg animate-pulse" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-heading font-medium">Order Management</h2>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Orders" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Orders</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="processing">Processing</SelectItem>
            <SelectItem value="shipped">Shipped</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {orders.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <ShoppingCart className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No orders found</p>
          </CardContent>
        </Card>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order ID</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((order) => (
              <TableRow key={order.order_id}>
                <TableCell className="font-medium">{order.order_id}</TableCell>
                <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                <TableCell>{order.items.length} item(s)</TableCell>
                <TableCell>${order.total.toFixed(2)}</TableCell>
                <TableCell>
                  <Badge variant="secondary">{order.payment_status}</Badge>
                </TableCell>
                <TableCell>
                  <Badge className={statusColors[order.status]}>{order.status}</Badge>
                </TableCell>
                <TableCell>
                  <Select 
                    value={order.status}
                    onValueChange={(value) => handleUpdateStatus(order.order_id, value)}
                  >
                    <SelectTrigger className="w-[130px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="processing">Processing</SelectItem>
                      <SelectItem value="shipped">Shipped</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// Main Admin Dashboard
export default function AdminDashboard() {
  const { user } = useAuth();
  const location = useLocation();

  const navItems = [
    { path: '/admin', label: 'Overview', icon: LayoutDashboard },
    { path: '/admin/onboarding', label: 'Onboarding', icon: ListChecks },
    { path: '/admin/roas', label: 'ROAS', icon: Target },
    { path: '/admin/vendors', label: 'Vendors', icon: Store },
    { path: '/admin/kyc', label: 'KYC Review', icon: Shield },
    { path: '/admin/products', label: 'Products', icon: Package },
    { path: '/admin/products/bulk', label: 'Bulk Upload', icon: Package },
    { path: '/admin/orders', label: 'Orders', icon: ShoppingCart },
    { path: '/admin/quotes', label: 'Quotes', icon: MessageSquare },
    { path: '/admin/testimonials', label: 'Testimonials', icon: BadgeCheck },
    { path: '/admin/referrals', label: 'Referrals', icon: Store },
    { path: '/admin/payouts', label: 'Payouts', icon: Wallet },
    { path: '/admin/featured-story', label: 'Featured Story', icon: Package },
    { path: '/admin/blog', label: 'Blog', icon: MessageSquare },
    { path: '/admin/broadcasts', label: 'Broadcasts', icon: Mail },
    { path: '/admin/commodities', label: 'Commodities', icon: Fuel },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 md:px-12 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-heading font-medium" data-testid="admin-dashboard">
            Admin Panel
          </h1>
        </div>

        {/* Navigation */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path}>
                <Button 
                  variant={isActive ? 'default' : 'ghost'}
                  className={isActive ? '' : 'text-muted-foreground'}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {item.label}
                </Button>
              </Link>
            );
          })}
        </div>

        <Routes>
          <Route index element={<AdminOverview />} />
          <Route path="onboarding" element={<VendorOnboardingAdmin />} />
          <Route path="roas" element={<CampaignRoasAdmin />} />
          <Route path="vendors" element={<AdminVendors />} />
          <Route path="kyc" element={<AdminKYC />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="products/bulk" element={<ProductBulkAdmin />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="quotes" element={<QuotesManager scope="admin" title="All Quote Requests" />} />
          <Route path="testimonials" element={<TestimonialsAdmin />} />
          <Route path="referrals" element={<ReferralsAdmin />} />
          <Route path="payouts" element={<ReferrerPayoutsAdmin />} />
          <Route path="featured-story" element={<FeaturedStoryAdmin />} />
          <Route path="blog" element={<BlogAdmin />} />
          <Route path="broadcasts" element={<EmailBroadcastAdmin />} />
          <Route path="commodities" element={<CommodityProductAdmin />} />
        </Routes>
      </main>

      <Footer />
    </div>
  );
}


// Admin KYC Review Component
function AdminKYC() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/kyc/pending`);
      setVendors(res.data.vendors || []);
    } catch (e) {
      toast.error('Failed to fetch KYC queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const viewDocument = async (fileId) => {
    try {
      const res = await axios.get(`${API}/admin/kyc/files/${fileId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Could not open document');
    }
  };

  const approve = async (vendorId) => {
    if (!window.confirm("Approve this vendor's KYC?")) return;
    try {
      await axios.put(`${API}/admin/vendors/${vendorId}/kyc/approve`);
      toast.success('KYC approved — vendor is now verified');
      fetchPending();
    } catch (e) {
      toast.error('Failed to approve');
    }
  };

  const submitReject = async (vendorId) => {
    if (!rejectReason || rejectReason.trim().length < 3) {
      toast.error('Please enter a reason (min 3 chars)');
      return;
    }
    try {
      await axios.put(`${API}/admin/vendors/${vendorId}/kyc/reject?reason=${encodeURIComponent(rejectReason.trim())}`);
      toast.success('KYC rejected');
      setRejecting(null);
      setRejectReason('');
      fetchPending();
    } catch (e) {
      toast.error('Failed to reject');
    }
  };

  if (loading) {
    return <div className="h-40 bg-muted rounded-lg animate-pulse" />;
  }

  return (
    <div className="space-y-4" data-testid="admin-kyc-page">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-heading font-semibold">KYC Review Queue</h2>
        <Badge variant="secondary" data-testid="kyc-queue-count">{vendors.length} pending</Badge>
      </div>

      {vendors.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            No vendors awaiting KYC review.
          </CardContent>
        </Card>
      ) : (
        vendors.map((v) => (
          <Card key={v.vendor_id} data-testid={`kyc-card-${v.vendor_id}`}>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle>{v.business_name}</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">{v.description}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Country: {v.country} • Submitted:{' '}
                    {v.kyc_submitted_at ? new Date(v.kyc_submitted_at).toLocaleString() : '—'}
                  </p>
                </div>
                <Badge className="bg-blue-100 text-blue-800">Pending Review</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  onClick={() => viewDocument(v.kyc_business_license_id)}
                  disabled={!v.kyc_business_license_id}
                  data-testid={`view-license-${v.vendor_id}`}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  View Business License
                </Button>
                <Button
                  variant="outline"
                  onClick={() => viewDocument(v.kyc_government_id)}
                  disabled={!v.kyc_government_id}
                  data-testid={`view-govid-${v.vendor_id}`}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  View Government ID
                </Button>
              </div>

              {rejecting === v.vendor_id ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    className="w-full px-3 py-2 border rounded-md text-sm"
                    placeholder="Reason for rejection (visible to vendor)"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    data-testid={`reject-reason-${v.vendor_id}`}
                  />
                  <div className="flex gap-2">
                    <Button size="sm" variant="destructive" onClick={() => submitReject(v.vendor_id)} data-testid={`confirm-reject-${v.vendor_id}`}>
                      Confirm Reject
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setRejecting(null); setRejectReason(''); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setRejecting(v.vendor_id)} data-testid={`reject-${v.vendor_id}`}>
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                  <Button onClick={() => approve(v.vendor_id)} data-testid={`approve-${v.vendor_id}`}>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve KYC
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
